import React from "react";
import { X } from "lucide-react";
import UserSelect from "./UserSelect";

/**
 * Pick several users: the selected users as removable chips, then the shared UserSelect (no
 * invite button) listing only users not yet selected. Choosing a user appends it and resets the
 * select. At `max` the select is hidden.
 *
 * Props:
 *   value       – array of selected user id strings
 *   onChange    – (ids: string[]) => void
 *   users       – array of user objects (as for UserSelect)
 *   loading     – boolean; disables the select while users load
 *   placeholder – select placeholder text
 *   disabled    – boolean; disables the select and the remove buttons
 *   max         – maximum number of selected users (default 10)
 */
const MultiUserSelect = ({
    value = [],
    onChange,
    users = [],
    loading = false,
    placeholder = "-- Add a user --",
    disabled = false,
    max = 10,
}) => {
    const selected = (value || []).map(String);
    const selectedSet = new Set(selected);
    const available = users.filter((u) => !selectedSet.has(String(u.id)));

    const labelFor = (id) => {
        const user = users.find((u) => String(u.id) === id);
        if (!user) return id;
        const name = user.fullName || user.full_name;
        return name ? `${name} (${user.email})` : user.email;
    };
    const nameFor = (id) => {
        const user = users.find((u) => String(u.id) === id);
        return user?.fullName || user?.full_name || user?.email || id;
    };

    const add = (id) => {
        if (!id || selectedSet.has(String(id)) || selected.length >= max) return;
        onChange([...selected, String(id)]);
    };
    const remove = (id) => onChange(selected.filter((s) => s !== id));

    return (
        <div className="flex flex-col gap-2" data-testid="multi-user-select">
            {selected.length > 0 && (
                <ul className="flex flex-wrap gap-1">
                    {selected.map((id) => (
                        <li
                            key={id}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-800 text-xs"
                            data-testid="multi-user-chip"
                        >
                            <span>{labelFor(id)}</span>
                            <button
                                type="button"
                                onClick={() => remove(id)}
                                disabled={disabled}
                                aria-label={`Remove ${nameFor(id)}`}
                                className="text-indigo-500 hover:text-indigo-800 disabled:opacity-50"
                            >
                                <X className="h-3 w-3" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            {selected.length >= max ? (
                <p className="text-xs text-content-secondary">Maximum of {max} assignees</p>
            ) : (
                <UserSelect
                    value=""
                    onChange={add}
                    users={available}
                    loading={loading}
                    disabled={disabled}
                    placeholder={placeholder}
                    className="text-sm"
                />
            )}
        </div>
    );
};

export default MultiUserSelect;
